import React from 'react';
import {
    Box,
    BoxProps,
    CloseIcon,
    DialogActions,
    DialogActionsProps,
    DialogTitle,
    DialogTitleProps,
    IconButton,
    IconButtonProps,
} from '@carrotfertility/carotene-core';

export type DialogCloseButtonProps = IconButtonProps<'button'>;

export function DialogCloseButton(props: DialogCloseButtonProps) {
    return (
        <Box display="flex" justifyContent="flex-end">
            <IconButton {...props}>
                <CloseIcon />
            </IconButton>
        </Box>
    );
}

export type DialogTitleImageProps = BoxProps<'img'>;

export function DialogTitleImage(props: DialogTitleImageProps) {
    return (
        <Box
            component="img"
            alt=""
            marginX="auto"
            marginY={(theme) => theme.spacing(theme.tokens.spacing.xs)}
            width="fit-content"
            {...props}
        />
    );
}

export type DialogTitleWithImageProps = DialogTitleProps<
    'span',
    {
        /**
         * The slot for your image component. Preferably using `DialogTitleImage`
         */
        image?: React.ReactNode;
    }
>;

export function DialogTitleWithImage(props: DialogTitleWithImageProps) {
    const { image, children, ...otherProps } = props;
    if (!image) {
        return <DialogTitle {...otherProps}>{children}</DialogTitle>;
    }
    return (
        <Box display="flex" flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.xs)}>
            {image}
            <DialogTitle {...otherProps}>{children}</DialogTitle>
        </Box>
    );
}

export interface DialogActionsBarProps extends DialogActionsProps {
    /**
     * The slot for your Primary Action. Most cases should be a `Button`
     */
    primaryAction: React.ReactNode;
    /**
     * The slot for your Secondary Action. Most cases should be an outlined, secondary `Button`
     */
    secondaryAction?: React.ReactNode;
    /**
     * The slot for your Tertiary Action. Most cases should be a text, secondary `Button` or a `Link`
     */
    tertiaryAction?: React.ReactNode;
}

export function DialogActionsBar(props: DialogActionsBarProps) {
    const { tertiaryAction, secondaryAction, primaryAction, ...otherProps } = props;
    return (
        <DialogActions {...otherProps}>
            {tertiaryAction ? (
                <Box
                    sx={(theme) => ({
                        marginRight: 'auto',
                        [theme.breakpoints.down('sm')]: {
                            marginRight: 'unset',
                        },
                    })}
                >
                    {tertiaryAction}
                </Box>
            ) : null}
            {secondaryAction}
            {primaryAction}
        </DialogActions>
    );
}
